// widgets/Sidebar/Sidebar.tsx
// Barre latérale droite listant toutes les sessions. Actions (resume/switch/close) : E4. Responsive : E5. Pastilles statut : E3.
// Phase 4 : rename inline (double-click) + création avec nom + sessions fermées collapsibles.

import './Sidebar.css'
import { useEffect, useRef, useState, useCallback } from 'react'
import { trpc } from '@/shared/lib/trpc'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useTabsStore } from '@/shared/stores/tabs.store'
import { useMessagesStore } from '@/shared/stores/messages.store'
import { useMetricsStore } from '@/shared/stores/metrics.store'
import { useCreateSession } from '@/features/create-session'
import { AgentsPanel } from '@/widgets/AgentsPanel/AgentsPanel'
import type { AgentStatus } from '@/shared/stores/status.store'

// Type issu du router sessions.list — Kysely retourne les dates comme Date
interface SessionSummary {
  id: string
  title: string | null
  status: 'created' | 'initializing' | 'active' | 'idle' | 'error' | 'closed'
  last_activity: Date
  space_slug: string | null
  sdk_session_id?: string | null
  model?: string | null
}

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
  /** Mobile overlay mode — rendu en position fixed plein écran */
  isOverlay?: boolean
  /** Callback pour fermer l'overlay mobile */
  onMobileClose?: () => void
}

interface SidebarItemProps {
  session: SessionSummary
  onClickSession: (session: SessionSummary) => void
  onClose: (sessionId: string) => void
  onRename: (sessionId: string, newTitle: string) => void
  isClosing: boolean
  isResuming: boolean
  resumeError: string | null
  onCreateAfterError: () => void
}

// Sous-composant interne — non exporté
function SidebarItem({
  session,
  onClickSession,
  onClose,
  onRename,
  isClosing,
  isResuming,
  resumeError,
  onCreateAfterError,
}: SidebarItemProps) {
  const title = session.title ?? 'Sans titre'
  const isClosed = session.status === 'closed'
  const isActive = session.status === 'active'

  // État inline rename
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(title)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus auto sur l'input quand on passe en mode édition
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  function handleDoubleClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (isResuming) return
    setEditValue(session.title ?? '')
    setIsEditing(true)
  }

  function commitRename() {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== (session.title ?? '')) {
      onRename(session.id, trimmed)
    }
    setIsEditing(false)
  }

  function cancelRename() {
    setIsEditing(false)
    setEditValue(session.title ?? '')
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitRename()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelRename()
    }
  }

  // Bouton croix : visible sur idle et error, masqué sur closed. Sur active, le bouton
  // reste cliquable — une confirmation s'affiche avant de fermer (voir onClick plus bas) ;
  // seul isClosing (fermeture déjà en vol) désactive réellement le bouton.
  const showCloseBtn = session.status !== 'closed'
  const closeDisabled = isClosing

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0 var(--space-1)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-3)',
          borderRadius: 'var(--radius-sm)',
          cursor: isEditing ? 'default' : (isResuming ? 'wait' : 'pointer'),
          minWidth: 0,
          opacity: isResuming ? 0.7 : 1,
        }}
        data-session-id={session.id}
        data-status={session.status}
        onClick={() => { if (!isResuming && !isEditing) onClickSession(session) }}
        onDoubleClick={handleDoubleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' && !isResuming && !isEditing) onClickSession(session) }}
      >
        {/* Pastille statut — E3 */}
        {isResuming ? (
          <span
            data-slot="status-dot"
            aria-label="Reprise en cours"
            style={{
              flexShrink: 0,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'var(--color-reconnecting)',
              animation: 'pulse 1.5s infinite',
            }}
          />
        ) : session.status !== 'closed' && (() => {
          let bg: string
          let animation: string | undefined

          switch (session.status) {
            case 'idle':
              bg = 'var(--color-online)'
              animation = undefined
              break
            case 'active':
              bg = 'var(--color-reconnecting)'
              animation = 'pulse 1.5s infinite'
              break
            case 'initializing':
            case 'created':
              bg = 'var(--color-reconnecting)'
              animation = undefined
              break
            case 'error':
              bg = 'var(--color-offline)'
              animation = undefined
              break
            default:
              return null
          }

          return (
            <span
              data-slot="status-dot"
              aria-label={session.status}
              style={{
                flexShrink: 0,
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: bg,
                ...(animation ? { animation } : {}),
              }}
            />
          )
        })()}

        {/* Titre ou input inline */}
        {isEditing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={commitRename}
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-body)',
              background: 'var(--color-bg-muted)',
              border: '1px solid var(--color-accent, var(--color-border))',
              borderRadius: 'var(--radius-sm)',
              padding: '1px var(--space-1)',
              outline: 'none',
              minWidth: 0,
            }}
          />
        ) : (
          <span
            style={{
              flex: 1,
              fontSize: 'var(--text-sm)',
              color: isClosed
                ? 'var(--color-text-muted)'
                : 'var(--color-text-body)',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
            title={isResuming ? undefined : title}
          >
            {isResuming ? 'Reprise…' : title}
          </span>
        )}

        {/* Bouton croix — close session */}
        {showCloseBtn && !isEditing && (
          <button
            aria-label="Fermer la session"
            title={
              isClosing
                ? 'Fermeture en cours…'
                : isActive
                  ? 'Session en cours de réponse — cliquer pour fermer quand même'
                  : 'Fermer la session'
            }
            disabled={closeDisabled}
            onClick={(e) => {
              e.stopPropagation()
              if (closeDisabled) return
              if (isActive) {
                const confirmed = window.confirm(
                  'Cette session est en cours de réponse. La fermer quand même ?'
                )
                if (!confirmed) return
              }
              onClose(session.id)
            }}
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'transparent',
              color: closeDisabled
                ? 'var(--color-text-faint)'
                : 'var(--color-text-muted)',
              cursor: closeDisabled ? 'not-allowed' : 'pointer',
              fontSize: 14,
              lineHeight: 1,
              padding: 0,
              opacity: isClosing ? 0.5 : 1,
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Message d'erreur resume — éphémère, affiché sous l'item */}
      {resumeError && (
        <div
          style={{
            padding: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-error)',
          }}
        >
          {resumeError}
          {resumeError.includes('ne peut pas être reprise') && (
            <button
              onClick={onCreateAfterError}
              style={{
                display: 'block',
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-secondary)',
                background: 'none',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '2px var(--space-2)',
                cursor: 'pointer',
              }}
            >
              Créer une nouvelle conversation
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Sous-composant inline input pour nommer une nouvelle session
interface NewSessionInputProps {
  onConfirm: (title: string) => void
  onCancel: () => void
}

function NewSessionInput({ onConfirm, onCancel }: NewSessionInputProps) {
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      onConfirm(value.trim())
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
    }
  }

  return (
    <div style={{ padding: 'var(--space-2) var(--space-3) var(--space-1)' }}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => onConfirm(value.trim())}
        placeholder="Nom de la conversation…"
        style={{
          width: '100%',
          fontSize: 'var(--text-sm)',
          color: 'var(--color-text-body)',
          background: 'var(--color-bg-muted)',
          border: '1px solid var(--color-accent, var(--color-border))',
          borderRadius: 'var(--radius-sm)',
          padding: '4px var(--space-2)',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-1)' }}>
        Entrée pour confirmer · Échap pour annuler
      </div>
    </div>
  )
}

function statusIsActive(status: AgentStatus | 'created'): boolean {
  return status !== 'closed'
}

const CLOSED_COLLAPSED_KEY = 'sidebar:closed-sessions-collapsed'

export function Sidebar({ isOpen: _isOpen, onToggle: _onToggle, isOverlay = false, onMobileClose }: SidebarProps) {
  const accessToken = useAuthStore(s => s.accessToken)
  const clearAuth = useAuthStore(s => s.clearAuth)
  const utils = trpc.useUtils()

  // Deconnexion — action deplacee ici depuis la barre visible mobile (backlog #10488,
  // redesign). Rendue uniquement en mode overlay (isOverlay), donc jamais sur desktop
  // ou le TopBar garde son propre bouton Sortir inchange.
  function handleLogout() {
    clearAuth()
    window.location.href = '/app/login'
  }

  const openExistingSession = useTabsStore(s => s.openExistingSession)
  const removeTab = useTabsStore(s => s.removeTab)
  const initSession = useMessagesStore(s => s.initSession)

  const { createSession, isLoading: isCreating } = useCreateSession()

  // État inline input pour nommer lors de la création
  const [showNewSessionInput, setShowNewSessionInput] = useState(false)

  // État collapse sections fermées — persisté en localStorage
  const [closedCollapsed, setClosedCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CLOSED_COLLAPSED_KEY) !== 'false'
    } catch {
      return true
    }
  })

  const toggleClosedCollapsed = useCallback(() => {
    setClosedCollapsed(prev => {
      const next = !prev
      try { localStorage.setItem(CLOSED_COLLAPSED_KEY, String(next)) } catch { /* ignore */ }
      return next
    })
  }, [])

  // État des mutations en cours par sessionId
  const [closingIds, setClosingIds] = useState<Set<string>>(new Set())
  const [resumingIds, setResumingIds] = useState<Set<string>>(new Set())
  // Erreurs ephémères de resume par sessionId
  const [resumeErrors, setResumeErrors] = useState<Record<string, string>>({})

  const { data: sessions, isError, isLoading } = trpc.sessions.list.useQuery(
    {},
    { enabled: !!accessToken },
  )

  // Mutations tRPC
  const closeMutation = trpc.sessions.close.useMutation({
    onSuccess: (_data, variables) => {
      const sessionId = variables.sessionId
      removeTab(sessionId)
      void utils.sessions.list.invalidate()
      setClosingIds(prev => { const next = new Set(prev); next.delete(sessionId); return next })
    },
    onError: (_err, variables) => {
      const sessionId = variables.sessionId
      setClosingIds(prev => { const next = new Set(prev); next.delete(sessionId); return next })
    },
  })

  const resumeMutation = trpc.sessions.resume.useMutation({
    onSuccess: (_data, variables) => {
      const sessionId = variables.sessionId
      const session = (sessions as SessionSummary[] | undefined)?.find(s => s.id === sessionId)
      const title = session?.title ?? 'Conversation'
      initSession(sessionId)
      useMetricsStore.getState().initSession(sessionId, session?.model ?? undefined)
      openExistingSession(sessionId, title)
      void utils.sessions.list.invalidate()
      setResumingIds(prev => { const next = new Set(prev); next.delete(sessionId); return next })
    },
    onError: (err, variables) => {
      const sessionId = variables.sessionId
      setResumingIds(prev => { const next = new Set(prev); next.delete(sessionId); return next })

      const message = err.message ?? ''
      const errorText = message.includes('sdk_session_id')
        ? 'Cette conversation ne peut pas être reprise. Créer une nouvelle conversation ?'
        : `Erreur lors de la reprise : ${message}`

      setResumeErrors(prev => ({ ...prev, [sessionId]: errorText }))
      setTimeout(() => {
        setResumeErrors(prev => {
          const next = { ...prev }
          delete next[sessionId]
          return next
        })
      }, 5000)
    },
  })

  const renameMutation = trpc.sessions.rename.useMutation({
    onSuccess: (_data, _variables) => {
      void utils.sessions.list.invalidate()
      // Pas d'émission locale — le serveur broadcast sur channel 'session' → session.handler gère
    },
    onError: (err) => {
      console.error('Rename failed:', err.message)
    },
  })

  // Gestionnaires d'actions

  function handleClickSession(session: SessionSummary) {
    onMobileClose?.()

    if (session.status === 'closed') {
      if (resumingIds.has(session.id)) return
      setResumingIds(prev => new Set(prev).add(session.id))
      setResumeErrors(prev => { const next = { ...prev }; delete next[session.id]; return next })
      resumeMutation.mutate({ sessionId: session.id })
    } else {
      const tabs = useTabsStore.getState().tabs
      const existing = tabs.find(t => t.sessionId === session.id)
      if (existing) {
        useTabsStore.getState().setActive(existing.id)
      } else {
        const title = session.title ?? 'Conversation'
        openExistingSession(session.id, title)
        initSession(session.id)
        useMetricsStore.getState().initSession(session.id, session.model ?? undefined)
      }
    }
  }

  function handleClose(sessionId: string) {
    if (closingIds.has(sessionId)) return
    setClosingIds(prev => new Set(prev).add(sessionId))
    closeMutation.mutate({ sessionId })
  }

  function handleRename(sessionId: string, newTitle: string) {
    renameMutation.mutate({ sessionId, title: newTitle })
  }

  // Création : ouvrir l'input inline
  function handleNewSessionClick() {
    if (isCreating || showNewSessionInput) return
    onMobileClose?.()
    setShowNewSessionInput(true)
  }

  function handleNewSessionConfirm(title: string) {
    setShowNewSessionInput(false)
    createSession({ title: title || 'Nouvelle conversation' })
    void utils.sessions.list.invalidate()
  }

  function handleNewSessionCancel() {
    setShowNewSessionInput(false)
  }

  // Tri côté client : actives d'abord (par last_activity desc), fermées ensuite (par last_activity desc)
  const { activeSessions, closedSessions } = (() => {
    if (!sessions) return { activeSessions: [], closedSessions: [] }

    const active: SessionSummary[] = []
    const closed: SessionSummary[] = []

    for (const s of sessions as SessionSummary[]) {
      if (statusIsActive(s.status)) {
        active.push(s)
      } else {
        closed.push(s)
      }
    }

    const byLastActivity = (a: SessionSummary, b: SessionSummary) =>
      new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime()

    return {
      activeSessions: active.sort(byLastActivity),
      closedSessions: closed.sort(byLastActivity),
    }
  })()

  const hasAnySessions = (sessions?.length ?? 0) > 0

  // Invalidation temps réel — E3
  // session:renamed retiré du bus (C6) — le serveur broadcast sur channel 'session' → session.handler invalide

  return (
    <aside
      data-widget="sidebar"
      className={isOverlay ? 'sidebar-overlay' : undefined}
      style={{
        width: 'var(--sidebar-width)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderLeft: isOverlay ? 'none' : '1px solid var(--color-border)',
        background: 'var(--color-bg-surface)',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Bouton fermer overlay mobile */}
      {isOverlay && onMobileClose && (
        <button
          className="sidebar-overlay-close"
          aria-label="Fermer"
          onClick={onMobileClose}
        >
          ×
        </button>
      )}

      {/* Header — bouton nouvelle session */}
      <div
        className="sidebar-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-3) var(--space-3)',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Sessions
        </span>
        {/* Bouton nouvelle session */}
        <button
          aria-label={isCreating ? 'Création en cours…' : 'Nouvelle session'}
          data-action="new-session"
          disabled={isCreating || showNewSessionInput}
          onClick={handleNewSessionClick}
          title={isCreating ? 'Création en cours…' : 'Nouvelle conversation'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-muted)',
            color: (isCreating || showNewSessionInput) ? 'var(--color-text-faint)' : 'var(--color-text-secondary)',
            cursor: (isCreating || showNewSessionInput) ? 'not-allowed' : 'pointer',
            fontSize: 18,
            lineHeight: 1,
            flexShrink: 0,
            opacity: (isCreating || showNewSessionInput) ? 0.5 : 1,
          }}
        >
          +
        </button>
      </div>

      {/* Input inline création */}
      {showNewSessionInput && (
        <NewSessionInput
          onConfirm={handleNewSessionConfirm}
          onCancel={handleNewSessionCancel}
        />
      )}

      {/* Liste sessions scrollable */}
      <div
        className="sidebar-list"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: 'var(--space-2) 0',
        }}
      >
        {isError && (
          <p
            style={{
              padding: 'var(--space-3)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-error)',
              margin: 0,
            }}
          >
            Impossible de charger les sessions
          </p>
        )}

        {!isError && isLoading && (
          <p
            style={{
              padding: 'var(--space-3)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-muted)',
              margin: 0,
            }}
          >
            Chargement…
          </p>
        )}

        {!isError && !isLoading && !hasAnySessions && (
          <p
            style={{
              padding: 'var(--space-3)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-muted)',
              margin: 0,
            }}
          >
            Aucune session
          </p>
        )}

        {activeSessions.length > 0 && (
          <>
            <div
              className="sidebar-section-label"
              style={{
                padding: 'var(--space-1) var(--space-3)',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--color-text-faint)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Sessions actives
            </div>
            {activeSessions.map(s => (
              <SidebarItem
                key={s.id}
                session={s}
                onClickSession={handleClickSession}
                onClose={handleClose}
                onRename={handleRename}
                isClosing={closingIds.has(s.id)}
                isResuming={resumingIds.has(s.id)}
                resumeError={resumeErrors[s.id] ?? null}
                onCreateAfterError={() => createSession()}
              />
            ))}
          </>
        )}

        {closedSessions.length > 0 && (
          <>
            {/* Chevron collapsibl — section fermées */}
            <button
              onClick={toggleClosedCollapsed}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                width: '100%',
                padding: 'var(--space-1) var(--space-3)',
                marginTop: activeSessions.length > 0 ? 'var(--space-2)' : 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  color: 'var(--color-text-faint)',
                  display: 'inline-block',
                  transform: closedCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms ease',
                  lineHeight: 1,
                }}
              >
                ▾
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--color-text-faint)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                Fermées ({closedSessions.length})
              </span>
            </button>

            {!closedCollapsed && closedSessions.map(s => (
              <SidebarItem
                key={s.id}
                session={s}
                onClickSession={handleClickSession}
                onClose={handleClose}
                onRename={handleRename}
                isClosing={closingIds.has(s.id)}
                isResuming={resumingIds.has(s.id)}
                resumeError={resumeErrors[s.id] ?? null}
                onCreateAfterError={() => createSession()}
              />
            ))}
          </>
        )}
      </div>

      {/* Footer — Deconnexion, mobile overlay uniquement (backlog #10488, redesign).
          Le TopBar masque son propre bouton Sortir en mobile ; l'action equivalente
          vit ici, dans le menu que le burger ouvre. Jamais rendu sur desktop
          (aside persistant, isOverlay=false) : n'affecte pas ce mode. */}
      {isOverlay && (
        <div
          className="sidebar-footer-logout"
          style={{
            flexShrink: 0,
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            padding: 'var(--space-2) var(--space-3)',
          }}
        >
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
              fontSize: 'var(--text-sm)',
              fontFamily: 'var(--font-sans)',
              padding: 'var(--space-2) var(--space-1)',
            }}
            title="Se deconnecter"
          >
            Sortir
          </button>
        </div>
      )}

      {/* Footer — slot Explorer V3.1 */}
      <div
        className="sidebar-footer"
        data-slot="explorer-v31"
        style={{
          flexShrink: 0,
          minHeight: 48,
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 var(--space-3)',
        }}
      >
        <AgentsPanel />
      </div>
    </aside>
  )
}
